import type { Currency } from '@ps/web-core-enums';
import { find } from 'lodash';
import { useEffect, useState } from 'react';
import { lastValueFrom } from 'rxjs';

import type { ObservableRequest } from '@swf/core/hooks/useRequest';
import useRequest from '@swf/core/hooks/useRequest';
import useWizardContext from '@swf/core/hooks/useWizardContext';

import type { Balance } from '../../../types/Cashier';
import { convertAmountByRate, getCurrencyRate } from '../../../utils/cashier';

interface Rate {
  curr: Currency;
  rate: number;
  invRate: number;
}

export type Rates = Rate[];

interface CurrencyFetcher {
  convertedAmount: number;
  rate: number;
  updateRate: (from: Currency, to: Currency) => Promise<void>;
}

const fetchRates = async (
  request: ObservableRequest,
  from: Currency | string,
  to: Currency | string
) => {
  try {
    const { body } = await lastValueFrom(
      request({
        service: 'wc3.currency.transferRates',
        body: { from, to }
      })
    );
    return body;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
  }
};

const useCurrencyFetcher = (
  amount: number,
  targetCurrency: Currency,
  fromCurrency: Currency
): CurrencyFetcher => {
  const { data } = useWizardContext();
  const request = useRequest();

  const [convertedAmount, setConvertedAmount] = useState(0);

  const [rate, setRate] = useState<number>(1);

  const updateRate = async (from: Currency, to: Currency) => {
    const response = await fetchRates(request, from, to);
    if (response) {
      setRate(getCurrencyRate(response[0]?.rates ?? [], to) ?? 1);
    }
  };

  useEffect(() => {
    const getRate = async () => {
      if (!data.initialRatesList || data.initialRatesList.length === 0) {
        const response = await fetchRates(request, fromCurrency, targetCurrency);
        if (response) {
          data.initialRatesList = response[0]?.rates;
          setRate(getCurrencyRate(response[0]?.rates ?? [], targetCurrency) ?? 1);
        }
      }
      const localRate = getCurrencyRate(data.initialRatesList, targetCurrency);
      setRate(localRate);
    };
    getRate();
  }, [data, fromCurrency, request, targetCurrency]);

  useEffect(() => {
    setConvertedAmount(convertAmountByRate(amount, rate, fromCurrency, false));
  }, [amount, targetCurrency, fromCurrency, rate]);

  return { convertedAmount, rate, updateRate };
};

export default useCurrencyFetcher;

export const getAvailableBalance = (
  enabledBalances: Balance[] | undefined,
  currency: string | undefined,
  isSiteSpecific: boolean | undefined
) => {
  const balance =
    enabledBalances && find(enabledBalances, (balance) => balance.currency === currency);

  return isSiteSpecific ? balance?.availableForSpending : balance?.balanceAvailable;
};
